/**
 * Olfacy hero carousel.
 * Scrolling/swiping is native CSS scroll-snap; this only wires arrows, dots and optional autoplay.
 * Layout reads are cached and refreshed on resize to avoid forced reflows during scroll.
 * With progress dashes (snippets/olfacy-progress-bars) autoplay is driven by the dash's CSS fill animation:
 * when it ends we advance; pausing freezes the fill, so it resumes where it stopped.
 */
class OlfacyHeroCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;

    this.slides = [...this.track.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.progress = Boolean(this.querySelector('[data-progress-bars]'));
    this.index = 0;
    this.offsets = [];
    this.ticking = false;
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.measure();
    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.track);

    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true), { signal });
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true), { signal });
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true), { signal }));

    this.setupAutoplay(signal);
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.resizeObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    this.stop();
  }

  measure() {
    const padStart = parseFloat(getComputedStyle(this.track).scrollPaddingInlineStart) || 0;
    this.offsets = this.slides.map((slide) => slide.offsetLeft - padStart);
    this.maxScroll = this.track.scrollWidth - this.track.clientWidth;
  }

  onScroll() {
    if (this.ticking || this.programmatic) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      const x = this.track.scrollLeft;
      let active = 0;
      if (x >= this.maxScroll - 2) {
        active = this.slides.length - 1;
      } else {
        let best = Infinity;
        this.offsets.forEach((offset, i) => {
          const distance = Math.abs(offset - x);
          if (distance < best) {
            best = distance;
            active = i;
          }
        });
      }
      this.setActive(active);
    });
  }

  setActive(i, restart = false) {
    if (i === this.index && !restart) return;
    this.dots[this.index]?.removeAttribute('aria-current');
    this.dots[i]?.setAttribute('aria-current', 'true');
    this.index = i;
    if (!this.progress) return;
    this.dots.forEach((dot) => dot.classList.remove('is-active'));
    const active = this.dots[i];
    if (active) {
      void active.offsetWidth; // restart the fill animation on this one small element
      active.classList.add('is-active');
    }
  }

  go(i, fromUser = false) {
    const count = this.slides.length;
    const target = (i + count) % count;
    if (fromUser && !this.progress) this.stop(true);
    // Ignore the in-between scroll positions of this smooth scroll, so the active dash doesn't flicker
    this.programmatic = true;
    clearTimeout(this.unlock);
    const release = () => {
      this.programmatic = false;
      clearTimeout(this.unlock);
    };
    this.track.addEventListener('scrollend', release, { once: true });
    this.unlock = setTimeout(release, 900);
    this.track.scrollTo({ left: Math.min(this.offsets[target], this.maxScroll) });
    this.setActive(target, true);
  }

  setupAutoplay(signal) {
    const delay = Number(this.dataset.autoplay);
    if (!delay || this.slides.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    if (this.progress) return this.setupProgressAutoplay(delay, signal);

    this.delay = delay;
    this.paused = false;
    this.visible = false;
    this.cancelled = false;

    const pause = () => {
      this.paused = true;
      this.stop();
    };
    const resume = () => {
      this.paused = false;
      this.start();
    };
    this.addEventListener('pointerenter', pause, { signal });
    this.addEventListener('pointerleave', resume, { signal });
    this.addEventListener('focusin', pause, { signal });
    this.addEventListener('focusout', resume, { signal });
    // Any touch/drag on the track counts as the visitor taking control
    this.track.addEventListener('pointerdown', () => this.stop(true), { signal, passive: true });
    this.track.addEventListener('wheel', () => this.stop(true), { signal, passive: true });
    document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()), { signal });

    this.visibilityObserver = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.visible ? this.start() : this.stop();
    });
    this.visibilityObserver.observe(this);
  }

  setupProgressAutoplay(delay, signal) {
    this.pauseReasons = new Set(['offscreen']);
    this.toggleAttribute('paused', true);
    this.style.setProperty('--ocp-duration', delay + 'ms');
    this.setAttribute('data-progress-autoplay', '');
    this.setActive(this.index, true);

    this.querySelector('[data-progress-bars]').addEventListener(
      'animationend',
      (event) => {
        if (event.target.closest('[data-dot]') === this.dots[this.index]) this.go(this.index + 1);
      },
      { signal }
    );
    const hold = (reason) => () => this.setPaused(reason, true);
    const release = (reason) => () => this.setPaused(reason, false);
    this.addEventListener('pointerenter', hold('hover'), { signal });
    this.addEventListener('pointerleave', release('hover'), { signal });
    this.addEventListener('focusin', hold('focus'), { signal });
    this.addEventListener('focusout', release('focus'), { signal });
    document.addEventListener('visibilitychange', () => this.setPaused('hidden', document.hidden), { signal });

    this.visibilityObserver = new IntersectionObserver(([entry]) => this.setPaused('offscreen', !entry.isIntersecting));
    this.visibilityObserver.observe(this);
  }

  setPaused(reason, on) {
    if (on) this.pauseReasons.add(reason);
    else this.pauseReasons.delete(reason);
    this.toggleAttribute('paused', this.pauseReasons.size > 0);
  }

  start() {
    if (!this.delay || this.cancelled || this.paused || !this.visible || document.hidden || this.timer) return;
    this.timer = setInterval(() => this.go(this.index + 1), this.delay);
  }

  stop(cancel = false) {
    if (cancel) this.cancelled = true;
    clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('olfacy-hero-carousel')) {
  customElements.define('olfacy-hero-carousel', OlfacyHeroCarousel);
}
